/**
 * QUIPWA — SessionScheduler
 *
 * Builds the prioritized card queue for a study session.
 *
 * Priority order within a session:
 *   1. Overdue cards (due < today), sorted by due ASC (most overdue first)
 *   2. Due today cards
 *   3. New cards (up to new_cards_per_day)
 *
 * Limits:
 *   - new_cards_per_day: 10–30, default 20
 *   - max_reviews_per_day: 200 hard cap
 *
 * Mixed mode ("Repasar ahora"): the same priority across every deck, with
 *   global limits — see buildMixedQueue.
 * Free mode: bypasses FSRS queue entirely, uses custom subset.
 *
 * Spec sections 6.1, 6.2
 */

import type { StateDBService } from '../db/state-db-service.ts';
import type { ReviewState, DeckInstance, UserSettings } from '../../types/domain.ts';
import { estimateSessionMinutes } from '../fsrs/fsrs-algorithm.ts';
import { logSession } from '../logger.ts';

export interface SessionCard {
  card_id: string;
  deck_instance_id: string;
  review_state: ReviewState;
  deck_name: string;
  /** 'overdue' | 'due' | 'new' — for UI display only */
  queue_type: 'overdue' | 'due' | 'new';
}

export interface SessionQueue {
  cards: SessionCard[];
  estimated_minutes: number;
  /** True if estimated_minutes > target_session_minutes */
  needs_split: boolean;
  /** How many cards were capped by max_reviews_per_day */
  capped_count: number;
}

export interface DeckWithInstance {
  instance: DeckInstance;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/** Fisher-Yates shuffle — interleave without bias */
function shuffleArray<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Merges per-deck lists into one, keeping each deck's own order (its priority:
 * most overdue first) and spreading the decks evenly. At every step the next
 * card comes from the deck furthest behind its share of the total; ties go to
 * the deck listed first.
 *
 *   A = [a1, a2], B = [b1, b2, b3, b4, b5, b6]  →  b1 a1 b2 b3 b4 a2 b5 b6
 *
 * Deterministic on purpose: the plan shown on Inicio and the session built a
 * moment later contain the same cards in the same order.
 */
export function interleaveByDeck(groups: SessionCard[][]): SessionCard[] {
  const lists = groups.filter((g) => g.length > 0);
  const total = lists.reduce((n, g) => n + g.length, 0);
  const taken = lists.map(() => 0);
  const out: SessionCard[] = [];

  while (out.length < total) {
    let best = 0;
    let bestLag = -Infinity;
    for (let i = 0; i < lists.length; i++) {
      if (taken[i] >= lists[i].length) continue;
      const lag = (lists[i].length / total) * (out.length + 1) - taken[i];
      if (lag > bestLag) {
        bestLag = lag;
        best = i;
      }
    }
    out.push(lists[best][taken[best]++]);
  }
  return out;
}

const nowSeconds = () => Math.floor(Date.now() / 1000);

// ─── SCHEDULER ───────────────────────────────────────────────────────────────

export class SessionScheduler {
  private readonly stateDB: StateDBService
  constructor(stateDB: StateDBService) { this.stateDB = stateDB }

  /**
   * Build a queue for a single-deck session (FLIP mode).
   */
  async buildSingleDeckQueue(
    instance: DeckInstance,
    settings: UserSettings
  ): Promise<SessionQueue> {
    const nowTs    = Math.floor(Date.now() / 1000);
    const todayEnd = nowTs + 86400;
    const maxRev   = settings.max_reviews_per_day;
    const maxNew   = settings.new_cards_per_day;

    // 1. Overdue: due strictly before nowTs
    logSession(`SingleDeck: fetching overdue cards... maxRev: ${maxRev}`);
    const overdueRaw = await this.stateDB.getDueCards(instance.id, nowTs - 1, maxRev);
    logSession(`SingleDeck: got ${overdueRaw.length} overdue from DB.`);
    const overdueSet = new Set(overdueRaw.map(rs => rs.card_id));
    const overdue: SessionCard[] = overdueRaw
      .map(rs => this.toSessionCard(rs, instance.deck_name, 'overdue'));

    // 2. Due today: due in [nowTs, todayEnd), skip already-overdue
    // we only need up to maxRev cards total between overdue and dueToday.
    let dueTodayRaw: ReviewState[] = [];
    const remainingRev = maxRev - overdue.length;
    if (remainingRev > 0) {
      logSession(`SingleDeck: fetching up to ${remainingRev + overdueSet.size} dueToday cards...`);
      dueTodayRaw = await this.stateDB.getDueCards(instance.id, todayEnd, remainingRev + overdueSet.size);
      logSession(`SingleDeck: got ${dueTodayRaw.length} dueToday from DB.`);
    }

    const dueToday: SessionCard[] = dueTodayRaw
      .filter(rs => !overdueSet.has(rs.card_id) && rs.due >= nowTs)
      .map(rs => this.toSessionCard(rs, instance.deck_name, 'due'));

    // 3. New cards (up to daily limit)
    logSession(`SingleDeck: fetching up to ${maxNew} new cards...`);
    const newRaw = await this.stateDB.getNewCards(instance.id, maxNew);
    logSession(`SingleDeck: got ${newRaw.length} new from DB.`);
    const newCards: SessionCard[] = newRaw.map(
      rs => this.toSessionCard(rs, instance.deck_name, 'new')
    );

    // Apply hard cap
    const combined = [...overdue, ...dueToday, ...newCards];
    logSession(`SingleDeck: combined length before slice = ${combined.length}`);
    let capped_count = 0;
    let cards = combined;
    if (combined.length > maxRev) {
      cards = combined.slice(0, maxRev);
      capped_count = combined.length - maxRev;
    }

    const estimated_minutes = estimateSessionMinutes(cards.length, 20);

    return {
      cards,
      estimated_minutes,
      needs_split: estimated_minutes > settings.target_session_minutes,
      capped_count
    };
  }

  /**
   * "Repasar ahora": everything due in every deck, as one FSRS session
   * (MIXED mode).
   *
   * The single-deck priority applies across decks: every overdue card first,
   * then today's, then new ones, each tier interleaved by deck. The limits
   * apply to the session as a whole:
   *   - at most `new_cards_per_day` new cards in total, not per deck (five
   *     decks would otherwise bring five days' worth of new cards);
   *   - `max_reviews_per_day` caps the queue from the end, so overdue cards
   *     are the last to be left out.
   *
   * The interleaving used to happen before the cap, which could leave overdue
   * cards out in favour of new ones.
   */
  async buildMixedQueue(
    instances: DeckInstance[],
    settings: UserSettings,
    nowTs: number = nowSeconds()
  ): Promise<SessionQueue> {
    const todayEnd = nowTs + 86400;
    const maxRev   = settings.max_reviews_per_day;
    const maxNew   = settings.new_cards_per_day;

    const overdue: SessionCard[][] = [];
    const dueToday: SessionCard[][] = [];
    const fresh: SessionCard[][] = [];

    for (const inst of instances) {
      // Ordered by due date, so the most overdue come first and survive the limit.
      const due = await this.stateDB.getDueCards(inst.id, todayEnd, maxRev);
      overdue.push(due.filter(rs => rs.due < nowTs).map(rs => this.toSessionCard(rs, inst.deck_name, 'overdue')));
      dueToday.push(due.filter(rs => rs.due >= nowTs).map(rs => this.toSessionCard(rs, inst.deck_name, 'due')));

      const newCards = maxNew > 0 ? await this.stateDB.getNewCards(inst.id, maxNew) : [];
      fresh.push(newCards.map(rs => this.toSessionCard(rs, inst.deck_name, 'new')));
    }

    const combined = [
      ...interleaveByDeck(overdue),
      ...interleaveByDeck(dueToday),
      ...interleaveByDeck(fresh).slice(0, maxNew),
    ];
    const cards = combined.slice(0, maxRev);
    const capped_count = combined.length - cards.length;

    const estimated_minutes = estimateSessionMinutes(cards.length, 20);

    return {
      cards,
      estimated_minutes,
      needs_split: estimated_minutes > settings.target_session_minutes,
      capped_count
    };
  }

  /**
   * Build a free queue (FREE mode) — bypasses FSRS scheduling.
   * User picks the deck and how many cards. Does NOT modify FSRS state.
   *
   * @param instance     The deck to study
   * @param count        Max number of cards
   * @param orderBy      'worst' = worst historical performance first; 'random' = shuffled
   */
  async buildFreeQueue(
    instance: DeckInstance,
    count: number,
    orderBy: 'worst' | 'random' = 'worst'
  ): Promise<SessionQueue> {
    logSession(`FreeQueue: fetching ${count} cards ordered by ${orderBy}...`);
    let cards: ReviewState[];

    if (orderBy === 'worst') {
      cards = await this.stateDB.getWorstCards(instance.id, count);
    } else {
      cards = await this.stateDB.getRandomCards(instance.id, count);
    }

    const sessionCards = cards.map(rs => this.toSessionCard(rs, instance.deck_name, 'due'));
    const estimated_minutes = estimateSessionMinutes(sessionCards.length, 20);

    return {
      cards: sessionCards,
      estimated_minutes,
      needs_split: false, // Free mode doesn't auto-split
      capped_count: 0
    };
  }

  /**
   * Build a free queue spanning MULTIPLE decks — bypasses FSRS scheduling,
   * same as buildFreeQueue but merges cards from every selected deck.
   *
   * @param instances    The decks to pull cards from
   * @param limitMode    'none' = all cards from every deck
   *                     'percent' = X% of each deck's own cards
   *                     'total' = merge everything, then cap to a global total
   * @param totalLimit   Used when limitMode === 'total'
   * @param percentLimit Used when limitMode === 'percent'
   */
  async buildFreeQueueMulti(
    instances: DeckInstance[],
    limitMode: 'none' | 'total' | 'percent',
    totalLimit: number,
    percentLimit: number,
    orderBy: 'worst' | 'random' = 'random'
  ): Promise<SessionQueue> {
    const groups: SessionCard[][] = [];

    for (const inst of instances) {
      const stats = await this.stateDB.getDeckStats(inst.id);
      const totalInDeck = stats?.total_cards ?? 0;
      if (totalInDeck === 0) continue;

      const count = limitMode === 'percent'
        ? Math.max(1, Math.ceil(totalInDeck * percentLimit / 100))
        : totalInDeck;

      logSession(`FreeQueueMulti: deck ${inst.deck_name} — fetching ${count}/${totalInDeck} cards (${limitMode}).`);
      const raw = orderBy === 'worst'
        ? await this.stateDB.getWorstCards(inst.id, count)
        : await this.stateDB.getRandomCards(inst.id, count);

      const cards = raw.map(rs => this.toSessionCard(rs, inst.deck_name, 'due'));
      if (cards.length > 0) groups.push(cards);
    }

    let merged = shuffleArray(groups.flat());
    if (limitMode === 'total') {
      merged = merged.slice(0, totalLimit);
    }

    const estimated_minutes = estimateSessionMinutes(merged.length, 20);

    return {
      cards: merged,
      estimated_minutes,
      needs_split: false, // Free mode doesn't auto-split
      capped_count: 0
    };
  }

  /**
   * Split a queue into segments that fit within target_session_minutes.
   * Returns the first segment; remaining cards are for subsequent sessions.
   */
  splitQueue(queue: SessionQueue, targetMinutes: number): {
    segment: SessionCard[];
    remaining: SessionCard[];
  } {
    logSession(`splitQueue called with targetMinutes: ${targetMinutes}, queue.length: ${queue.cards.length}`);
    const validTarget = targetMinutes && targetMinutes > 0 ? targetMinutes : 15;
    const cardsPerSegment = Math.floor((validTarget * 60) / 20); // 20s per card
    logSession(`cardsPerSegment calculated as: ${cardsPerSegment}`);
    return {
      segment: queue.cards.slice(0, cardsPerSegment),
      remaining: queue.cards.slice(cardsPerSegment)
    };
  }

  private toSessionCard(
    rs: ReviewState,
    deckName: string,
    queueType: 'overdue' | 'due' | 'new'
  ): SessionCard {
    return {
      card_id: rs.card_id,
      deck_instance_id: rs.deck_instance_id,
      review_state: rs,
      deck_name: deckName,
      queue_type: queueType
    };
  }
}
